import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import { Empty } from '@caios/ui';
import { DeliveryClients, DeliveryTabs, useDeliveryClient } from '../delivery-nav.tsx';
import { MonthlyResults, MonthPicker, count, nowMonth, pct, shiftMonth, times, usd, type MonthResult } from '../components/monthly-results.tsx';
import type { Me } from '../auth.ts';

/**
 * Delivery › Results (11-03, approved canvas option 5): every client on ads in a month — spend, conversions, cost per
 * conversion, value, ROAS, ROI — biggest spend first, with what needs fixing in their tracking; one client opens their own
 * month (the same view the client sees in the portal, where our managers can also enter the month's numbers).
 */
type Row = MonthResult & { account_id: string; name: string };
const sum = (rows: Row[], f: (r: Row) => number | null | undefined) => rows.reduce((s, r) => s + (f(r) ?? 0), 0);

export function Results() {
  const { me } = useRouteContext({ from: '/_app' }) as { me: Me };
  const manage = ['owner', 'admin', 'manager'].includes(me.role);
  const dc = useDeliveryClient();
  const [mode, setMode] = useState<'all' | 'one'>(manage ? 'all' : 'one');
  const [month, setMonth] = useState(() => shiftMonth(nowMonth(), new Date().getUTCDate() < 8 ? -1 : 0));
  const all = useQuery({ queryKey: ['results-overview', month], enabled: manage && mode === 'all', queryFn: async () => {
    const r = await fetch(`/api/v1/delivery/results/overview?month=${month}`, { credentials: 'include' });
    const j = await r.json(); if (!r.ok) throw new Error(j.error ?? `Request failed (${r.status})`);
    return j as { month: string; clients: Row[] };
  } });
  const rows = all.data?.clients ?? [];
  const open = (id: string) => { dc.setAccount(id); setMode('one'); };
  // Picking a client in the side panel opens that client.
  const seen = useRef(dc.account);
  useEffect(() => { if (seen.current && dc.account !== seen.current) setMode('one'); seen.current = dc.account; }, [dc.account]);
  return (
    <div className="cx-split">
      <DeliveryClients d={dc} testid="rs-client" />
      <main style={{ minWidth: 0 }} className="cx-stack">
        <header className="cx-page-head cx-rise" style={{ marginBottom: 0 }}>
          <div><div className="cx-eyebrow"><b>Results</b> — spend, conversions and return, month by month</div><h1 className="cx-display">{mode === 'all' ? `${new Date(`${month}-01T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })} — every client on ads` : (dc.client?.name ?? 'Results')}</h1></div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {manage && <div className="cx-seg" role="group" aria-label="Show"><button type="button" aria-pressed={mode === 'all'} onClick={() => setMode('all')} data-testid="rs-all">All clients</button>
              <button type="button" aria-pressed={mode === 'one'} onClick={() => setMode('one')} data-testid="rs-one">{dc.client?.name ?? 'One client'}</button></div>}
            {mode === 'all' && <MonthPicker month={month} onChange={setMonth} />}
          </div>
        </header>
        <DeliveryTabs />
        {mode === 'one' && (dc.account ? <MonthlyResults key={dc.account} accountId={dc.account} title={dc.client?.name ?? ''} /> : <Empty icon="harbor" title="Pick a client">Their results show here.</Empty>)}
        {mode === 'all' && all.isError && <Empty icon="harbor" title="Results aren’t available">{(all.error as Error).message}</Empty>}
        {mode === 'all' && all.data && (rows.length === 0
          ? <Empty icon="harbor" title="No client had ad spend this month">Clients show here once their Google or Meta ads spend, or once someone enters their month’s numbers.</Empty>
          : <>
            <section className="mr-small" data-testid="rs-totals">
              {[['Ad spend, all clients', usd(sum(rows, (r) => r.totals.spend))], ['Conversions', count(sum(rows, (r) => r.totals.conversions))], ['Value booked', usd(sum(rows, (r) => r.totals.value))],
                ['Clients with something to fix', String(rows.filter((r) => r.flags.length).length)]].map(([k, v]) => <div key={k} className="mr-tile mr-tile--small"><small>{k}</small><b>{v}</b></div>)}
            </section>
            <div className="mr-card"><div className="cx-table-wrap"><table className="cx-table mr-table" data-testid="rs-table">
              <thead><tr><th>Client</th><th>Spend</th><th>Conversions</th><th>Cost / conv.</th><th>Value</th><th>ROAS</th><th>ROI</th></tr></thead>
              <tbody>{rows.map((r) => (
                <tr key={r.account_id} data-testid="rs-row" style={{ cursor: 'pointer' }} onClick={() => open(r.account_id)}>
                  <td><button type="button" className="cx-link-btn" onClick={(e) => { e.stopPropagation(); open(r.account_id); }}><b>{r.name}</b></button>
                    {r.flags.map((f) => <small key={f.kind} className="mr-warn-text">{f.text}</small>)}{r.partial === 'from' && <small>partial — from {r.tracking_from}</small>}</td>
                  <td>{usd(r.totals.spend)}</td><td>{count(r.totals.conversions)}</td><td>{usd(r.totals.cpa, true)}</td><td>{usd(r.totals.value)}</td>
                  <td><b className={r.flags.some((f) => f.kind === 'value_not_set') ? 'mr-bad' : undefined}>{times(r.totals.roas)}</b></td><td>{pct(r.totals.roi)}</td>
                </tr>))}</tbody>
            </table></div></div>
          </>)}
      </main>
    </div>
  );
}
